import { useCallback, useEffect, useState } from "react";
import { DesignTokens, Row, Col, Button, UndoIcon, RangePicker } from "melco-ui";

import { useTranslation } from "react-i18next";
import { isEmpty, some } from "lodash-es";
import { MachineLicensesListFiltersType } from "../../../hooks/machineLicenses/MachineLicensesListFiltersType";
import styled from "styled-components";
import { Search } from "../../search/Search";
import { MachineLicenseOptionFilter } from "./filter/MachineLicenseOptionFilter";
import { MachineLicensesStatusTypeFilter } from "./filter/MachineLicenseStatusFilter";
import dayjs from "dayjs";


const { spacings } = DesignTokens;
const SelectBoxWrapper = styled.div`
  max-width: 300px;
  min-width: 225px;
`;

type MachineLicensesListFilterProps = {
  onFiltersChange: (filters: MachineLicensesListFiltersType | undefined) => void;
};

export const MachineLicensesListFilter: React.FC<MachineLicensesListFilterProps> = ({
  onFiltersChange,
}) => {
  const { t } = useTranslation();
  const [machineLicensesFilter, setMachineLicensesFilter] = useState<
    MachineLicensesListFiltersType | undefined
  >(undefined);

  const [searchValue, setSearchValue] = useState("");
  const [userNameValue, setUserNameValue] = useState("");

  const [licenseGroupIdValue, setLicenseGroupIdValue] = useState("");
  const [licenseKeyValue, setLicenseKeyValue] = useState("");

  useEffect(() => {
    onFiltersChange(machineLicensesFilter);
  }, [machineLicensesFilter, onFiltersChange]);

  const resetFilter = useCallback(() => {
    setSearchValue("");
    setUserNameValue("")
    setLicenseGroupIdValue("");
    setLicenseKeyValue("");
    setMachineLicensesFilter(undefined);
  }, []);

  return (
    <div>
      <Row gutter={[spacings.middle, spacings.middle]}>
        <Col xs={24} xl={6}>
          <Search
            onSearchInputChange={(value) => {
              setMachineLicensesFilter((prevFilter) => {
                return { ...prevFilter, searchText: value };
              });
            }}
            onChange={(e) => setSearchValue(e.target.value)}
            value={searchValue}
          />
        </Col>
        <Col xs={24} xl={6}>
          <Search
            onSearchInputChange={(value) => {
              setMachineLicensesFilter((prevFilter) => {
                return { ...prevFilter, userName: value };
              });
            }}
            onChange={(e) => setUserNameValue(e.target.value)}
            value={userNameValue}
            placeholder={t("machinelicenses.list.columns.user_name")}
          />
        </Col>
        <Col xs={24} xl={6}>
          <Search
            onSearchInputChange={(value) => {
              setMachineLicensesFilter((prevFilter) => {
                return { ...prevFilter, transactionId: value };
              });
            }}
            onChange={(e) => setLicenseGroupIdValue(e.target.value)}
            value={licenseGroupIdValue}
            placeholder={t("machinelicenses.list.columns.transaction_id")}
          />
        </Col>
        <Col xs={24} xl={6}>
          <Search
            onSearchInputChange={(value) => {
              setMachineLicensesFilter((prevFilter) => {
                return { ...prevFilter, licenseKey: value };
              });
            }}
            onChange={(e) => setLicenseKeyValue(e.target.value)}
            value={licenseKeyValue}
            placeholder={t("machinelicenses.filters.license_key")}
          />
        </Col>
        <Col xs={24} xl={6}>
          <MachineLicenseOptionFilter
            onChange={(value) => {
              setMachineLicensesFilter((prevFilter) => {
                return {
                  ...prevFilter,
                  subType: !isEmpty(value) ? value : undefined,
                };
              });
            }}
            value={machineLicensesFilter?.subType ?? undefined}
          />
        </Col>
        <Col xs={24} md={6} xl={6}>
          <MachineLicensesStatusTypeFilter
            onChange={(value) => {
              setMachineLicensesFilter((prevFilter) => {
                return {
                  ...prevFilter,
                  status: !isEmpty(value) ? value : undefined,
                };
              });
            }}
            value={machineLicensesFilter?.status ?? undefined}
          />
        </Col>
        <Col xs={24} xl={6} md={6}>

          <SelectBoxWrapper>
            <RangePicker
              format={"L"}
              onChange={(value) => {
                const expiresOnStartTimeStamp =
                  (value && value[0] && value[0].startOf("day").toDate()) ??
                  undefined;
                const expiresOnEndTimeStamp =
                  (value && value[1] && value[1].endOf("day").toDate()) ??
                  undefined;

                setMachineLicensesFilter((prevFilter) => {
                  return {
                    ...prevFilter,
                    expiresOnStartTimeStamp,
                    expiresOnEndTimeStamp,
                  };
                });
              }}
              value={
                machineLicensesFilter?.expiresOnStartTimeStamp &&
                machineLicensesFilter?.expiresOnEndTimeStamp && [
                  dayjs(machineLicensesFilter?.expiresOnStartTimeStamp),
                  dayjs(machineLicensesFilter?.expiresOnEndTimeStamp),
                ]
              }
              placeholder={[
                t("machinelicenses.filters.expires_on_from"),
                t("machinelicenses.filters.expires_on_to"),
              ]}
            />
          </SelectBoxWrapper>
        </Col>
        <Col xs={24} xl={6} md={6}>

          <SelectBoxWrapper>
            <RangePicker
              format={"L"}
              onChange={(value) => {
                const renewsOnStartTimeStamp =
                  (value && value[0] && value[0].startOf("day").toDate()) ??
                  undefined;
                const renewsOnEndTimeStamp =
                  (value && value[1] && value[1].endOf("day").toDate()) ??
                  undefined;

                setMachineLicensesFilter((prevFilter) => {
                  return {
                    ...prevFilter,
                    renewsOnStartTimeStamp,
                    renewsOnEndTimeStamp,
                  };
                });
              }}
              value={
                machineLicensesFilter?.renewsOnStartTimeStamp &&
                machineLicensesFilter?.renewsOnEndTimeStamp && [
                  dayjs(machineLicensesFilter?.renewsOnStartTimeStamp),
                  dayjs(machineLicensesFilter?.renewsOnEndTimeStamp),
                ]
              }
              placeholder={[
                t("machinelicenses.filters.renews_on_from"),
                t("machinelicenses.filters.renews_on_to"),
              ]}
            />
          </SelectBoxWrapper>
        </Col>
        <Col xs={24} xl={6}>
          <Row gutter={[spacings.middle, spacings.middle]}>


            <Col>
              {some(machineLicensesFilter) && (
                <Button
                  icon={<UndoIcon />}
                  type="primary"
                  onClick={resetFilter}
                  data-cy-id="machinelicenses-filter-reset"
                  style={{ minWidth: "100%", marginLeft: "auto" }}
                >
                  {t("filter.reset.button", { ns: "melco-common" })}
                </Button>
              )}
            </Col>
          </Row>
        </Col>


      </Row>
      {/* </RowWrapper> */}
    </div>
  );
};
